import { el } from "./dom.js";

// Shared building blocks for the auth screens, so the four logged-out views
// don't repeat markup. Everything renders inside a `.auth` wrapper styled by
// styles/auth.css to the MAGAZINE look: a full-bleed colour-bloom "cover", a
// giant Anton MEDIA/lime-TRACKER masthead with a tagline, and the form inside a
// single frosted-glass card. The auth screens do NOT show the app's top bar.

// The screen shell: the magazine cover (masthead + tagline) over a frosted
// glass card. The card holds an optional back chevron, an Anton heading (title +
// accent word), and the passed children (form fields, message, links).
//   authShell({ title: "Welcome", titleEm: "back", onBack }, ...nodes)
export function authShell({ title, titleEm, onBack }, ...children) {
	// Masthead — MEDIA + lime TRACKER, the new product identity.
	const masthead = el(
		"div.auth__masthead",
		{},
		"MEDIA",
		el("span.auth__masthead-accent", {}, "TRACKER"),
	);
	const tagline = el(
		"p.auth__tagline",
		{},
		"Your collection. Every screen. One account.",
	);

	const cover = el("header.auth__cover", {}, masthead, tagline);

	// Card heading (e.g. "Welcome back", "Create an account").
	const titleNode = el(
		"h1.auth__title",
		{},
		title,
		titleEm ? " " : null,
		titleEm ? el("em", {}, titleEm) : null,
	);

	const backNode = onBack
		? el(
				"button.auth__back",
				{ type: "button", "aria-label": "Back", onclick: onBack },
				"‹",
			)
		: null;

	// The title and (optional) back chevron live in a header row, kept OUTSIDE
	// `.auth__body` so views that swap the body (forgot-password's "sent"
	// confirmation) leave the heading in place.
	const cardHead = el("div.auth__head", {}, backNode, titleNode);

	const card = el(
		"div.auth__card",
		{},
		cardHead,
		el("div.auth__body", {}, ...children),
	);

	return el("div.auth", {}, cover, card);
}

export function authInput(props = {}) {
	return el("input.auth-input", { autocomplete: "off", ...props });
}

// A labelled field. `hint` may be a node returned from fieldHint() for live
// validation feedback, or omitted.
export function authField(labelText, control, hint) {
	return el(
		"label.auth-field",
		{},
		el("span.auth-field__label", {}, labelText),
		control,
		hint || null,
	);
}

// A per-field hint line with setters for neutral / ok / bad states.
export function fieldHint(initial = "") {
	const node = el("div.auth-field__hint", {}, initial);
	return {
		node,
		set(text, state) {
			node.textContent = text || "";
			node.classList.remove("auth-field__hint--ok", "auth-field__hint--bad");
			if (state === "ok") node.classList.add("auth-field__hint--ok");
			if (state === "bad") node.classList.add("auth-field__hint--bad");
		},
	};
}

export function authButton(label, props = {}) {
	const { primary, ...rest } = props;
	return el(
		`button.auth-btn${primary ? ".auth-btn--primary" : ""}`,
		{ type: "button", ...rest },
		label,
	);
}

// Form-level message (errors from the server, success confirmations). Returns
// the node plus setters; lives at the top of the form body.
export function authMessage() {
	const node = el("div.auth-msg", {});
	function show(text, kind) {
		node.textContent = text || "";
		node.classList.remove("auth-msg--bad", "auth-msg--ok");
		if (text) node.classList.add(kind === "ok" ? "auth-msg--ok" : "auth-msg--bad");
	}
	return {
		node,
		error: (text) => show(text, "bad"),
		ok: (text) => show(text, "ok"),
		clear: () => show("", null),
	};
}

// Text links row beneath the primary action (e.g. "Forgot password?", "Sign up").
export function authLinks(...links) {
	return el("div.auth-links", {}, ...links);
}

export function authLink(children, onClick) {
	return el(
		"button.auth-link",
		{ type: "button", onclick: onClick },
		...(Array.isArray(children) ? children : [children]),
	);
}

// Minimum password length (matches the sign-up spec). Supabase's own default is
// 6; we hold to 8 in the UI.
export const MIN_PASSWORD = 8;

// A password strength meter (track + label) with an update(pw) method. Shared by
// the sign-up and reset-password screens. Scoring is intentionally simple: length
// is the dominant factor, with a nudge for character variety.
export function strengthMeter() {
	const fill = el("div.auth-strength__fill", {});
	const label = el("span.auth-strength__label", {}, "");
	const node = el(
		"div.auth-strength",
		{},
		el("div.auth-strength__track", {}, fill),
		label,
	);

	// Magazine palette: hot pink → amber → lime.
	const LEVELS = [
		{ pct: "33%", color: "#ff3d8b", text: "Weak" },
		{ pct: "66%", color: "#ffd60a", text: "Okay" },
		{ pct: "100%", color: "#b6f23d", text: "Strong" },
	];

	function update(pw) {
		if (!pw) {
			fill.style.width = "0";
			label.textContent = "";
			return;
		}
		if (pw.length < MIN_PASSWORD) {
			fill.style.width = "20%";
			fill.style.background = "#ff3d8b";
			label.textContent = `Min ${MIN_PASSWORD}`;
			return;
		}
		let score = 1;
		if (pw.length >= 12) score++;
		const variety =
			(/[a-z]/.test(pw) ? 1 : 0) +
			(/[A-Z]/.test(pw) ? 1 : 0) +
			(/[0-9]/.test(pw) ? 1 : 0) +
			(/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
		if (variety >= 3) score++;
		const level = LEVELS[Math.min(score, LEVELS.length) - 1];
		fill.style.width = level.pct;
		fill.style.background = level.color;
		label.textContent = level.text;
	}

	return { node, update };
}

// Bottom toast, reused as a singleton on <body> so it survives the navigation
// that clears #app (used for the "password updated" confirmation on /sign-in).
export function showAuthToast(text) {
	let toast = document.getElementById("auth-toast");
	if (!toast) {
		toast = el(
			"div.auth-toast",
			{ id: "auth-toast" },
			el("span.auth-toast__mark", {}, "✓"),
			el("span.auth-toast__text", {}, ""),
		);
		document.body.append(toast);
	}
	toast.querySelector(".auth-toast__text").textContent = text;
	// Force reflow so re-triggering the transition works on a reused node.
	void toast.offsetWidth;
	toast.classList.add("show");
	clearTimeout(showAuthToast._t);
	showAuthToast._t = setTimeout(() => toast.classList.remove("show"), 2400);
}
